import { useTranslation } from 'react-i18next';
import type { ClientStat, PosDevice } from './clientStats.js';
import {
  daysSince,
  deviceKey,
  freshness,
  ongoingSilenceMinutes,
} from './clientStats.js';
import { formatAmd, resolveAppLocale } from '../locale.js';
import { Sheet, StatusPill } from '../ui/index.js';

const YEREVAN = 'Asia/Yerevan';

function formatWhen(iso: string | null, locale: string, dash: string): string {
  if (!iso) return dash;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return dash;
  return date.toLocaleString(locale, {
    timeZone: YEREVAN,
    dateStyle: 'medium',
    timeStyle: 'short',
  });
}

export function ClientStatsSheet({
  name,
  stat,
  devices,
  offlineMinutes30d,
  diagnostics30d,
  onClose,
}: {
  name: string;
  stat: ClientStat | undefined;
  devices: PosDevice[];
  offlineMinutes30d: Record<string, number>;
  diagnostics30d: number;
  onClose: () => void;
}) {
  const { t, i18n } = useTranslation();
  const locale = resolveAppLocale(i18n.language);
  const dash = t('common.emptyDash');

  function ago(iso: string | null): string {
    const days = daysSince(iso);
    if (days === null) return t('clients.never');
    if (days === 0) return t('clients.freshToday');
    if (days === 1) return t('clients.freshYesterday');
    return t('clients.freshDays', { n: days });
  }

  function seen(iso: string): string {
    const minutes = Math.floor((Date.now() - new Date(iso).getTime()) / 60_000);
    if (!Number.isFinite(minutes) || minutes < 2) return t('clients.seenJustNow');
    if (minutes < 60) return t('clients.seenMinutes', { n: minutes });
    const hours = Math.floor(minutes / 60);
    if (hours < 48) return t('clients.seenHours', { n: hours });
    return t('clients.seenDays', { n: Math.floor(hours / 24) });
  }

  function duration(minutes: number): string {
    if (minutes <= 0) return dash;
    if (minutes < 60) return t('clients.minutesShort', { n: minutes });
    const hours = Math.floor(minutes / 60);
    const rest = minutes % 60;
    if (rest === 0) return t('clients.hoursShort', { n: hours });
    return t('clients.hoursMinutes', { hours, minutes: rest });
  }

  function platform(value: string): string {
    if (value === 'macos') return t('clients.platformMacos');
    if (value === 'windows') return t('clients.platformWindows');
    if (value === 'web') return t('clients.platformWeb');
    return t('clients.platformDesktop');
  }

  function cell(label: string, value: string, alert = false) {
    return (
      <div>
        <dt>{label}</dt>
        <dd className={alert ? 'client-stat-alert' : undefined}>{value}</dd>
      </div>
    );
  }

  const orderFresh = freshness(stat?.last_order_at ?? null);

  return (
    <Sheet wide open title={name} caption={stat ? ago(stat.last_order_at) : undefined} onClose={onClose}>
      {!stat ? (
        <p className="muted">{t('common.noData')}</p>
      ) : (
        <div className="client-stat-sections">
          <section className="client-stat-section">
            <h3>{t('clients.sectionActivity')}</h3>
            <dl className="client-stat-grid">
              {cell(t('clients.colLastOrder'), ago(stat.last_order_at))}
              {cell(t('clients.ordersToday'), String(stat.orders_today))}
              {cell(t('clients.orders7'), String(stat.orders_7d))}
              {cell(t('clients.orders30'), String(stat.orders_30d))}
              {cell(t('clients.openShifts'), String(stat.open_shifts))}
              {cell(t('clients.lastShift'), formatWhen(stat.last_shift_at, locale, dash))}
              {cell(t('clients.lastSignIn'), formatWhen(stat.last_sign_in_at, locale, t('clients.never')))}
              {cell(t('clients.revenue30'), formatAmd(stat.revenue_30d, i18n.language))}
            </dl>
            {orderFresh === 'quiet' && (
              <p className="client-stat-note">{t('clients.statsQuietSub')}</p>
            )}
          </section>

          <section className="client-stat-section">
            <h3>{t('clients.sectionHealth')}</h3>
            <dl className="client-stat-grid">
              {cell(t('clients.force30'), String(stat.force_30d), stat.force_30d > 0)}
              {cell(t('clients.forceSystem'), String(stat.force_system_error_30d), stat.force_system_error_30d > 0)}
              {cell(t('clients.forceMistake'), String(stat.force_mistake_30d))}
              {cell(t('clients.forceFree'), String(stat.force_free_30d))}
              {cell(t('clients.forceOther'), String(stat.force_other_30d), stat.force_other_30d > 0)}
              {cell(t('clients.paidNoFiscal'), String(stat.paid_no_fiscal_30d), stat.paid_no_fiscal_30d > 0)}
              {cell(t('clients.paidNoPayment'), String(stat.paid_no_payment_30d), stat.paid_no_payment_30d > 0)}
              {cell(t('clients.diagnostics30'), String(diagnostics30d), diagnostics30d > 0)}
            </dl>
          </section>

          <section className="client-stat-section">
            <h3>{t('clients.sectionUsage')}</h3>
            <dl className="client-stat-grid">
              {cell(t('clients.cashiers'), String(stat.cashiers))}
              {cell(t('clients.dashboardUsers'), String(stat.dashboard_users))}
              {cell(t('clients.products'), String(stat.products))}
              {cell(t('clients.categories'), String(stat.categories))}
              {cell(t('clients.ingredients'), String(stat.ingredients))}
              {cell(t('clients.recipes'), String(stat.recipes))}
              {cell(t('clients.loyaltyMembers'), String(stat.loyalty_members))}
              {cell(t('clients.bookingsMonth'), String(stat.bookings_month))}
              {cell(t('clients.invoicesMonth'), String(stat.invoices_month))}
            </dl>
          </section>

          <section className="client-stat-section">
            <h3>{t('clients.sectionRegisters')}</h3>
            {devices.length === 0 ? (
              <p className="muted" style={{ margin: 0 }}>{t('clients.registersEmpty')}</p>
            ) : (
              <table className="table table-cards">
                <thead>
                  <tr>
                    <th>{t('clients.colPlatform')}</th>
                    <th>{t('clients.colVersion')}</th>
                    <th>{t('clients.colLastSeen')}</th>
                    <th>{t('clients.colPending')}</th>
                    <th>{t('clients.colLastGap')}</th>
                    <th>{t('clients.colOffline30')}</th>
                  </tr>
                </thead>
                <tbody>
                  {devices.map((device) => {
                    const quiet = ongoingSilenceMinutes(device.last_seen_at);
                    const offline =
                      (offlineMinutes30d[deviceKey(stat.client_id, device.device_id)] ?? 0) + quiet;
                    return (
                      <tr key={device.device_id}>
                        <td className="table-cards-title" data-label={t('clients.colPlatform')}>
                          {platform(device.platform)}
                        </td>
                        <td data-label={t('clients.colVersion')}>{device.app_version}</td>
                        <td data-label={t('clients.colLastSeen')}>
                          <StatusPill
                            status={quiet > 0 ? 'waiting' : 'active'}
                            tone={quiet > 24 * 60 ? 'danger' : quiet > 0 ? 'warning' : 'success'}
                          >
                            {seen(device.last_seen_at)}
                          </StatusPill>
                        </td>
                        <td data-label={t('clients.colPending')}>{device.pending_outbox}</td>
                        <td data-label={t('clients.colLastGap')}>{duration(device.last_gap_minutes)}</td>
                        <td data-label={t('clients.colOffline30')}>{duration(offline)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </section>
        </div>
      )}
    </Sheet>
  );
}
